// assets/resources/js/core/autoload.js

import {qsa} from './dom';

const inited = new WeakMap();

/**
 * Парсит data-* атрибуты (кроме data-module) в объект с camelCase ключами.
 * Примитивно преобразует строки в логические, числовые значения или JSON.
 * @param {HTMLElement} el
 * @returns {Record<string, any>}
 */
function parseDataOptions(el) {
    const opts = {};
    for (const {name, value} of Array.from(el.attributes)) {
        if (!name.startsWith('data-') || name === 'data-module') continue;

        const key = name.slice(5).replace(/-([a-z])/g, (_, c) => c.toUpperCase());

        let v = value;
        if (v === 'true') v = true; else if (v === 'false') v = false; else if (!Number.isNaN(Number(v)) && v.trim() !== '') v = Number(v); else if ((v.startsWith('{') && v.endsWith('}')) || (v.startsWith('[') && v.endsWith(']'))) {
            try {
                v = JSON.parse(v);
            } catch (e) { /* ignore parse error, keep as string */
            }
        }

        opts[key] = v;
    }
    return opts;
}

/**
 * Инициализирует модуль по имени на элементе.
 * @param {HTMLElement} el
 * @param {string} name
 */
async function initModuleOn(el, name) {
    const s = inited.get(el) || new Set();
    if (s.has(name)) return;

    try {
        const mod = await import(/* webpackChunkName: "mod-[request]" */ `../modules/${name}.js`);
        const fn = mod?.default;

        if (typeof fn === 'function') {
            const options = parseDataOptions(el);
            const ret = fn(el, options);

            s.add(name);
            inited.set(el, s);

            // Сохранение функции destroy, если она была возвращена
            if (ret && typeof ret.destroy === 'function') {
                const prev = el._moduleDestroyers || {};
                prev[name] = ret.destroy;
                el._moduleDestroyers = prev;
            }
        }
    } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
            console.warn(`[autoload] Module "${name}" not found`, e);
        }
    }
}

/**
 * Извлекает имена модулей из значения атрибута data-module.
 * @param {string} attrValue
 * @returns {string[]}
 */
function getModuleNames(attrValue) {
    return attrValue
        .split(/[,\s]+/)
        .filter(s => s.trim().length > 0);
}

/**
 * Инициализирует модули на элементах с атрибутом data-module.
 * Может использовать IntersectionObserver для "ленивой" загрузки.
 * @param {object} [config]
 * @param {boolean} [config.lazy=false]
 * @param {HTMLElement | Document} [config.root=document]
 */
export async function autoloadModules({lazy = false, root = document} = {}) {
    const els = qsa('[data-module]', root);

    if (lazy && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
            for (const e of entries) {
                if (!e.isIntersecting) continue;
                const names = getModuleNames(e.target.getAttribute('data-module'));
                names.forEach(n => initModuleOn(e.target, n));
                io.unobserve(e.target);
            }
        }, {rootMargin: '200px'});

        els.forEach(el => io.observe(el));
        return;
    }

    await Promise.all(els.map(async (el) => {
        const names = getModuleNames(el.getAttribute('data-module'));
        for (const name of names) {
            await initModuleOn(el, name);
        }
    }));
}

// -----------------------------------------------------------------------------
// Дополнительно: Автоматическое уничтожение модулей при удалении из DOM
// -----------------------------------------------------------------------------

const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
        if (m.removedNodes.length === 0) continue;

        m.removedNodes.forEach(node => {
            if (!(node instanceof HTMLElement)) return;

            const withModules = node.matches?.('[data-module]') ? [node] : qsa('[data-module]', node);

            withModules.forEach(el => {
                const destroyers = el._moduleDestroyers;
                if (destroyers) {
                    Object.values(destroyers).forEach(fn => {
                        try {
                            fn();
                        } catch (e) { /* ignore destroy error */
                        }
                    });
                    el._moduleDestroyers = null;
                }
            });
        });
    }
});

mo.observe(document.documentElement, {childList: true, subtree: true});
